/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { statusCategories } from "@Features/portal-settings";
import {
  ReactFlow,
  Controls,
  Background,
  MarkerType,
  applyNodeChanges,
  Handle,
  Position,
  getSmoothStepPath,
  getStraightPath,
  EdgeLabelRenderer,
  BaseEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { getStatusListInTransitions, getTransitionsList, setWorkflowResourceType } from "../WorkflowHelper";
import type { WorkflowResourceType } from "../WorkflowHelper";
import IconComponent from "@CubeComponents/icons";
import Box from "@mui/material/Box";

import { entityData } from "../WorkflowConstants";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { RootState } from "@Reducers/index";

const CustomTransitionEdge = ({
  id: _id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
}: any) => {
  const isVerticallyAligned = Math.abs(sourceX - targetX) < 20;

  let edgePath, labelX, labelY;

  if (isVerticallyAligned) {
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
  } else {
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 20,
    });
  }

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <Box
          style={{
            position: "absolute",
            // labelX/Y are SVG edge midpoint coordinates computed by react-flow's
            // getBezierPath; they are pixel offsets in SVG user-units, not design tokens.
            // eslint-disable-next-line no-px/no-px
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: "0.75rem",
            fontWeight: 500,
            background: "white",
            padding: "0.125rem 0.375rem",
            borderRadius: "0.1875rem",
            border: "1px solid #ddd",
            pointerEvents: "all",
          }}
          className="nodrag nopan"
        >
          {data?.label || ""}
        </Box>
      </EdgeLabelRenderer>
    </>
  );
};

const CustomNode = ({ data, id: _id }: any) => {
  const { sourceHandles = [], targetHandles = [], ...nodeData } = data;

  const getHandlePositions = (handleCount: number) => {
    if (handleCount === 1) {
      return [50];
    } else if (handleCount === 2) {
      return [30, 70];
    } else if (handleCount === 3) {
      return [20, 50, 80];
    } else {
      const positions: any[] = [];
      const range = 70;
      const step = range / (handleCount - 1);
      for (let i = 0; i < handleCount; i++) {
        positions.push(15 + i * step);
      }
      return positions;
    }
  };

  const sourcePositions = getHandlePositions(sourceHandles.length);
  const targetPositions = getHandlePositions(targetHandles.length);

  return (
    <Box
      style={nodeData.style}
      className="px-3 py-2 rounded text-center min-w-[10.75rem] min-h-9 flex items-center justify-center relative"
    >
      {sourceHandles.map((handle: any, index: number) => (
        <Handle
          key={`source-${handle.transitionId}-${handle.id}`}
          type="source"
          position={Position.Bottom}
          id={handle.id}
          style={{
            insetInlineStart: `${sourcePositions[index]}%`,
            bottom: -8,
            width: "0.5rem",
            height: "0.5rem",
            backgroundColor: "#666A84",
          }}
        />
      ))}

      {targetHandles.map((handle: any, index: number) => (
        <Handle
          key={`target-${handle.transitionId}-${handle.id}`}
          type="target"
          position={Position.Top}
          id={handle.id}
          style={{
            insetInlineStart: `${targetPositions[index]}%`,
            top: -8,
            width: "0.5rem",
            height: "0.5rem",
            backgroundColor: "#666A84",
          }}
        />
      ))}

      <span>{nodeData.label}</span>
    </Box>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  customTransition: CustomTransitionEdge,
};

function TransitionsDiagram({ workflowId }: { readonly workflowId: string }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const entityRef = useRef<any>(null);

  const resourceType = useSelector((state: RootState) => state.workflowResourceType as WorkflowResourceType);

  const [nodes, setNodes] = useState<any>([]);
  const [edges, setEdges] = useState<any>([]);
  const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const nodeWidth = 172;
  const nodeHeight = 36;

  const getLayoutedElements = (nodes: any, edges: any) => {
    dagreGraph.setGraph({
      rankdir: "TB",
      nodesep: 100,
      ranksep: 120,
      marginx: 40,
      marginy: 40,
    });

    for (const node of nodes) {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    }

    for (const edge of edges) {
      dagreGraph.setEdge(edge.source, edge.target);
    }

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node: any) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
      return node;
    });

    return { nodes: layoutedNodes, edges };
  };

  const connectorStrokeColor = "#666A84";

  const onChangeEntity = (args: any) => {
    dispatch(setWorkflowResourceType(args.value));
    setIsLoadingComplete(false);
  };

  const getNodeColor = (category: any) => {
    const matchedCategory = statusCategories.find((record: any) => record.name === category);
    const backgroundColor = matchedCategory ? matchedCategory.backgroundHexcode : "#0006A2";
    return backgroundColor;
  };

  const processTransitions = (transitions: any, statuses: any) => {
    const nodes: any = [];
    const edges: any = [];

    const _fromStatusIds = transitions.map((t: any) => t.statusFrom._id);
    const _toStatusIds = transitions.map((t: any) => t.statusTo._id);

    const outgoingConnections = new Map<string, any[]>();
    const incomingConnections = new Map<string, any[]>();

    for (const transition of transitions) {
      const sourceId = transition.statusFrom._id;
      const targetId = transition.statusTo._id;

      if (!outgoingConnections.has(sourceId)) {
        outgoingConnections.set(sourceId, []);
      }
      if (!incomingConnections.has(targetId)) {
        incomingConnections.set(targetId, []);
      }

      outgoingConnections.get(sourceId)!.push(transition);
      incomingConnections.get(targetId)!.push(transition);
    }

    statuses.map((status: any) => {
      let nodeType = "custom";

      const outgoing = outgoingConnections.get(status._id) || [];
      const incoming = incomingConnections.get(status._id) || [];

      const sourceHandles = outgoing.map((transition: any, index: number) => ({
        id: `source-${index}`,
        transitionId: transition._id || `${transition.statusFrom._id}-${transition.statusTo._id}-${index}`,
      }));

      const targetHandles = incoming.map((transition: any, index: number) => ({
        id: `target-${index}`,
        transitionId: transition._id || `${transition.statusFrom._id}-${transition.statusTo._id}-${index}`,
      }));

      const nodeColor = getNodeColor(status.category);

      nodes.push({
        id: status._id,
        type: nodeType,
        data: {
          label: status.name,
          sourceHandles,
          targetHandles,
          style: {
            backgroundColor: nodeColor,
            color: "white",
            border: "0",
          },
        },
        category: status.category,
      });
    });

    for (const [index, transition] of transitions.entries()) {
      const sourceId = transition.statusFrom._id;
      const targetId = transition.statusTo._id;

      const sourceNode = nodes.find((n: any) => n.id === sourceId);
      const targetNode = nodes.find((n: any) => n.id === targetId);

      if (sourceNode && targetNode) {
        const sourceTransitions = outgoingConnections.get(sourceId) || [];
        const targetTransitions = incomingConnections.get(targetId) || [];

        const sourceHandleIndex = sourceTransitions.findIndex(
          (t: any) =>
            (t._id && t._id === transition._id) ||
            (!t._id &&
              t.statusFrom._id === transition.statusFrom._id &&
              t.statusTo._id === transition.statusTo._id &&
              sourceTransitions.indexOf(t) === sourceTransitions.indexOf(transition)),
        );

        const targetHandleIndex = targetTransitions.findIndex(
          (t: any) =>
            (t._id && t._id === transition._id) ||
            (!t._id &&
              t.statusFrom._id === transition.statusFrom._id &&
              t.statusTo._id === transition.statusTo._id &&
              targetTransitions.indexOf(t) === targetTransitions.indexOf(transition)),
        );

        const uniqueId = transition._id || `${transition.statusFrom._id}+${transition.statusTo._id}+${index}`;

        edges.push({
          id: uniqueId,
          source: sourceId,
          target: targetId,
          sourceHandle: `source-${sourceHandleIndex}`,
          targetHandle: `target-${targetHandleIndex}`,
          type: "customTransition",
          data: { label: transition.name },
          style: {
            stroke: connectorStrokeColor,
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: connectorStrokeColor,
          },
        });
      }
    }

    return {
      nodes,
      edges,
    };
  };

  const fetchTransitionsData = async () => {
    const response = await getTransitionsList({
      value: { params: { workflowId, resourceType } },
    });
    return response.data;
  };

  const fetchStatusListInTransitions = async () => {
    const response = await getStatusListInTransitions({
      workflowId,
      resourceType,
    });
    return response.data;
  };

  const fetchData = async () => {
    const transitions = await fetchTransitionsData();
    const statuses = await fetchStatusListInTransitions();

    if (transitions.status && statuses.status) {
      const transitionEdges = processTransitions(transitions.result, statuses.result);
      const layoutElements = getLayoutedElements(transitionEdges.nodes, transitionEdges.edges);
      setEdges(layoutElements.edges);
      setNodes(layoutElements.nodes);
      setIsLoadingComplete(true);
      return;
    }

    dispatch(
      showToastNotification({
        content: t("Internal Server Error"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, [resourceType]);

  const getIllustration = () => (
    <Box className="flex h-full w-full flex-col items-center justify-center gap-6 bg-white">
      <IconComponent name="NoTransitionIllustration" />
    </Box>
  );

  const onNodesChange = useCallback((changes: any) => setNodes((nds: any) => applyNodeChanges(changes, nds)), []);

  const getDiagramComponent = useCallback(
    () => (
      <Box
        className="w-full h-full border-s border-e border-b"
        style={{
          borderColor: "rgb(236 243 254 / var(--tw-border-opacity, 1))",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
          onNodesChange={onNodesChange}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </Box>
    ),
    [nodes, edges, onNodesChange],
  );

  return (
    <Box className="flex h-full flex-row gap-6 overflow-auto">
      <Box className="flex h-full w-full flex-col overflow-auto">
        <Box className="flex pt-5 ps-8 pb-5 border border-solid border-b-2 border-button_light bg-system_colour_43">
          <AutoComplete
            ref={entityRef}
            options={entityData.dataSource}
            valueField="value"
            width="12.5rem"
            getOptionLabel={(option: any) => option?.name || ""}
            onChange={(_event: any, newValue: any) => onChangeEntity({ value: newValue?.value, itemData: newValue })}
            value={entityData.dataSource.find((opt: any) => opt.value === resourceType) || null}
            renderInput={(params: any) => <TextFieldComponent {...params} />}
          />
        </Box>
        {isLoadingComplete && edges.length ? getDiagramComponent() : undefined}
        {isLoadingComplete && !edges.length ? getIllustration() : undefined}
      </Box>
    </Box>
  );
}

export default TransitionsDiagram;
